import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CheckRounded,
  CloseRounded,
  FactCheckRounded,
  Inventory2Rounded,
  LocalShippingRounded,
  OpenInNewRounded,
  ReceiptLongRounded,
} from '@mui/icons-material';
import { StatusChip } from '../../../common/Table/Table.styles';
import { usePermission } from '../../../../hooks/usePermission';
import { useRelatedEntityNavigation } from '../../../../hooks/useRelatedEntityNavigation';
import { INVENTORY_PERMISSIONS } from '../../../../constants/permissions.constants';
import { SaleFulfillmentGroup } from '../../../../types/sales.types';
import {
  FULFILLMENT_TIMELINE_STEPS,
  FulfillmentTimelineStep,
  getGroupTimeline,
  TimelineNodeState,
} from '../../../../utils/sales/saleFulfillmentPresentation';

const STEP_ICONS: Record<FulfillmentTimelineStep['key'], React.ElementType> = {
  request: ReceiptLongRounded,
  approval: FactCheckRounded,
  transit: LocalShippingRounded,
  received: Inventory2Rounded,
};

const DocumentLink: React.FC<{
  code: string;
  canView: boolean;
  onClick: () => void;
}> = ({ code, canView, onClick }) =>
  canView ? (
    <Tooltip title="Abrir en su módulo">
      <Box
        component="button"
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          padding: 0,
          font: 'inherit',
          color: 'primary.main',
          fontWeight: 700,
          textDecoration: 'underline',
          '&:hover': { opacity: 0.8 },
        }}
      >
        {code}
        <OpenInNewRounded sx={{ fontSize: 13 }} />
      </Box>
    </Tooltip>
  ) : (
    <Typography component="span" variant="body2" fontWeight={700}>
      {code}
    </Typography>
  );

/** Resolves the visual colors for a single timeline node given its state. */
const useNodeColors = () => {
  const theme = useTheme();
  return (state: TimelineNodeState) => {
    switch (state) {
      case 'done':
        return { fill: theme.palette.success.main, text: '#fff', ring: 'transparent' };
      case 'active':
        return {
          fill: '#6366F1',
          text: '#fff',
          ring: alpha('#6366F1', 0.22),
        };
      case 'failed':
        return { fill: theme.palette.error.main, text: '#fff', ring: 'transparent' };
      case 'pending':
      default:
        return {
          fill: theme.palette.mode === 'dark' ? alpha('#fff', 0.08) : theme.palette.grey[200],
          text: theme.palette.text.disabled,
          ring: 'transparent',
        };
    }
  };
};

interface TimelineProps {
  nodes: TimelineNodeState[];
  dense?: boolean;
  /** Optional related-document node rendered under a given step (e.g. SOL under
   * "Solicitud", TRL under "En tránsito"). */
  stepExtras?: Partial<Record<FulfillmentTimelineStep['key'], React.ReactNode>>;
}

const Timeline: React.FC<TimelineProps> = ({ nodes, dense, stepExtras }) => {
  const theme = useTheme();
  const nodeColors = useNodeColors();
  const size = dense ? 26 : 32;
  const iconSize = dense ? 15 : 18;

  const connectorColor = (leftState: TimelineNodeState | undefined) =>
    leftState === 'done' ? theme.palette.success.main : theme.palette.divider;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', mt: dense ? 0.5 : 1 }}>
      {FULFILLMENT_TIMELINE_STEPS.map((step, index) => {
        const state = nodes[index];
        const colors = nodeColors(state);
        const StepIcon = STEP_ICONS[step.key];
        const isFirst = index === 0;
        const isLast = index === FULFILLMENT_TIMELINE_STEPS.length - 1;
        const extra = stepExtras?.[step.key];
        const nodeIcon =
          state === 'done' ? (
            <CheckRounded sx={{ fontSize: iconSize }} />
          ) : state === 'failed' ? (
            <CloseRounded sx={{ fontSize: iconSize }} />
          ) : (
            <StepIcon sx={{ fontSize: iconSize }} />
          );

        return (
          <Box
            key={step.key}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Connector halves flank the node so circles stay evenly spaced
                regardless of the related-document width rendered below. */}
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: isFirst ? 'transparent' : connectorColor(nodes[index - 1]),
                  transition: 'background-color 160ms ease-out',
                }}
              />
              <Box
                sx={{
                  flexShrink: 0,
                  width: size,
                  height: size,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: colors.fill,
                  color: colors.text,
                  boxShadow: colors.ring !== 'transparent' ? `0 0 0 4px ${colors.ring}` : 'none',
                  border:
                    state === 'pending'
                      ? `1px solid ${theme.palette.divider}`
                      : '1px solid transparent',
                  transition: 'all 160ms ease-out',
                }}
              >
                {nodeIcon}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: isLast ? 'transparent' : connectorColor(state),
                  transition: 'background-color 160ms ease-out',
                }}
              />
            </Box>

            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                fontSize: dense ? '0.65rem' : '0.7rem',
                fontWeight: state === 'active' ? 700 : 500,
                color:
                  state === 'pending'
                    ? 'text.disabled'
                    : state === 'failed'
                      ? 'error.main'
                      : 'text.secondary',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {step.label}
            </Typography>

            {extra ? (
              <Box
                sx={{
                  mt: 0.25,
                  px: 0.5,
                  maxWidth: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  textAlign: 'center',
                  fontSize: dense ? '0.65rem' : '0.72rem',
                  '& > *': { maxWidth: '100%' },
                }}
              >
                {extra}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

interface SaleFulfillmentTimelineProps {
  groups: SaleFulfillmentGroup[];
  dense?: boolean;
}

/**
 * Timeline/stepper visualization of mixed-branch fulfillment. Renders one card
 * per remote source location, each showing the SOL→TRL→reception progression.
 * Replaces the previous flat table for a clearer, action-oriented layout.
 */
export const SaleFulfillmentTimeline: React.FC<SaleFulfillmentTimelineProps> = ({
  groups,
  dense = false,
}) => {
  const canViewRequests = usePermission(INVENTORY_PERMISSIONS.REQUEST.VIEW);
  const canViewTransfers = usePermission(INVENTORY_PERMISSIONS.TRANSFER.VIEW);
  const { navigateToRelatedEntity } = useRelatedEntityNavigation();

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: dense ? 1 : 2 }}>
        No hay piezas remotas asociadas a esta venta.
      </Typography>
    );
  }

  const totalPieces = groups.reduce((acc, group) => acc + group.items.length, 0);
  const destinationName = groups[0]?.destinationLocation?.name;

  return (
    <Box sx={{ p: dense ? 1 : 0 }}>
      {/* Parent node: ties every remote group below to this single order. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mb: dense ? 1 : 1.25,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant={dense ? 'caption' : 'body2'} color="text.secondary">
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {totalPieces} pieza(s) remota(s)
          </Box>{' '}
          desde {groups.length} sucursal(es)
          {destinationName ? (
            <>
              {' '}
              hacia{' '}
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {destinationName}
              </Box>
            </>
          ) : null}
        </Typography>
      </Box>

      {/* Children: one card per source location, hung off a vertical tree line. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: dense ? 1 : 1.5,
        }}
      >
        {groups.map((group) => {
          const timeline = getGroupTimeline(group.status, {
            hasRequest: !!group.inventoryRequest,
            hasTransfer: !!group.inventoryTransfer,
            requestStatus: group.inventoryRequest?.status,
            transferStatus: group.inventoryTransfer?.status,
          });

          // Related documents are now hung under their matching timeline step:
          // SOL under "Solicitud" and TRL under "En tránsito".
          const stepExtras: Partial<Record<FulfillmentTimelineStep['key'], React.ReactNode>> = {
            request: group.inventoryRequest ? (
              <DocumentLink
                code={group.inventoryRequest.requestCode}
                canView={canViewRequests}
                onClick={() =>
                  navigateToRelatedEntity(
                    'INVENTORY_REQUEST',
                    group.inventoryRequest!.id,
                    group.inventoryRequest!.requestCode,
                  )
                }
              />
            ) : undefined,
            transit: group.inventoryTransfer ? (
              <DocumentLink
                code={group.inventoryTransfer.transferCode}
                canView={canViewTransfers}
                onClick={() =>
                  navigateToRelatedEntity(
                    'INVENTORY_TRANSFER',
                    group.inventoryTransfer!.id,
                    group.inventoryTransfer!.transferCode,
                  )
                }
              />
            ) : undefined,
          };

          return (
            <Box key={group.id}>
              <Box
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: dense ? 1.25 : 1.75,
                  bgcolor: theme.palette.background.paper,
                })}
              >
                {/* Header: route + piece count + status chip */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 0.75,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      {group.sourceLocation.name}
                      <Box component="span" sx={{ color: 'text.disabled', mx: 0.75 }}>
                        →
                      </Box>
                      {group.destinationLocation.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.items.length} pieza(s)
                    </Typography>
                  </Box>
                  <StatusChip
                    label={timeline.label}
                    status={timeline.statusType}
                    size="small"
                    visualStyle={timeline.failed ? 'elevated' : 'quiet'}
                    withIcon={timeline.failed}
                  />
                </Box>

                {/* Pieces in this group, each with its unique code. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: dense ? 0.75 : 1 }}>
                  {group.items.map((item) => (
                    <Chip
                      key={item.id}
                      size="small"
                      variant="outlined"
                      label={
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'baseline' }}
                        >
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {item.productCodeUnique}
                          </Box>
                          <Box component="span" sx={{ color: 'text.secondary' }}>
                            · {item.productName}
                          </Box>
                        </Box>
                      }
                      sx={{
                        height: dense ? 20 : 24,
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          px: 0.75,
                          fontSize: dense ? '0.65rem' : '0.72rem',
                        },
                      }}
                    />
                  ))}
                </Box>

                <Timeline nodes={timeline.nodes} dense={dense} stepExtras={stepExtras} />

                {group.failureReason && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ display: 'block', mt: 1, fontWeight: 600 }}
                  >
                    {group.failureReason}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SaleFulfillmentTimeline;
